import React, { FC, useState, useEffect } from 'react'
import { ChevronsDown } from 'react-feather'

/**Components */
import { useAuthState, Button, useShowMessage, CommentBox } from '@pepper/ui'

import { createThread } from '@pepper/utils/src/api/assignments/assignments'

import styles from './_.module.css'

interface Props {
  assignmentId: string
  refetch: () => void
  disabled?: boolean
  keepSamePage?: boolean
}

const AddComment: FC<Props> = ({
  assignmentId,
  refetch,
  disabled = false,
  keepSamePage,
}) => {
  const [jumpedToLatestVisible, setJumpToLatestVisible] =
    useState<boolean>(false)
  const [formData, setFormData] = useState<{
    comment: string
    attachments: Attachment[]
  }>({
    comment: '',
    attachments: [],
  })

  const { user } = useAuthState()
  const showMessage = useShowMessage()

  /** hide `jump to latest` option if user reached at bottom
   * this option only visible if page have scroll or multiple activities
   * this option help to jump on latest activity (Bottom of the page)
   */
  useEffect(() => {
    const mainTag = document.getElementsByTagName('main')
    const mainElement = mainTag?.[0]

    /** check if page have scroll to set default value */
    setTimeout(() => {
      if (
        mainElement.scrollHeight >
        window.innerHeight +
          (window.innerHeight * 50) /
            100 /** scroll should be more than 50% of windows innerWidth */
      ) {
        setJumpToLatestVisible(true)
      }
    }, 500)

    /** Hide option at bottom */
    let _isOptionVisible = true
    const _checkScrollToBottom = () => {
      if (
        mainElement.scrollHeight <
        window.innerHeight + (window.innerHeight * 50) / 100
      )
        return

      if (
        mainElement.offsetHeight + mainElement.scrollTop >=
          mainElement.scrollHeight - 100 &&
        _isOptionVisible
      ) {
        _isOptionVisible = false
        setJumpToLatestVisible(_isOptionVisible)
      } else if (
        mainElement.offsetHeight + mainElement.scrollTop <
          mainElement.scrollHeight - 100 &&
        !_isOptionVisible
      ) {
        _isOptionVisible = true
        setJumpToLatestVisible(_isOptionVisible)
      }
    }
    mainElement.addEventListener('scroll', _checkScrollToBottom)

    return () => {
      mainElement.removeEventListener('scroll', _checkScrollToBottom)
    }
  }, [])

  const scrollToBottom = () => {
    const mainTag = document.getElementsByTagName('main')
    mainTag && mainTag?.[0].scrollTo(0, mainTag?.[0].scrollHeight)
  }

  const handleSubmit = async () => {
    try {
      setFormData({ comment: '', attachments: [] })
      const { status, error } = await createThread({
        id: assignmentId,
        type: 'other',
        attachments: formData.attachments
          .filter((file: Attachment) => file.id)
          .map((attachment: Attachment) => attachment.id),
        text: formData.comment.trim() ?? '',
      })
      if (status === 'ok') {
        refetch()
        setTimeout(scrollToBottom, 200)
      } else {
        throw new Error(error)
      }
    } catch (error) {
      showMessage({ type: 'error', message: error.message })
    }
  }

  return (
    <div className={styles.commentFields}>
      {jumpedToLatestVisible && !keepSamePage ? (
        <div className={styles.jumpToLatestContainer}>
          <Button
            className={styles.jumpToLatest}
            type="default"
            onClick={() => scrollToBottom()}
            ghost={true}
          >
            Jump to latest activity &nbsp;
            <ChevronsDown size={16} />
          </Button>
        </div>
      ) : null}
      <CommentBox
        avatar={user.avatar}
        onSubmit={handleSubmit}
        onChange={(value: { comment: string; attachments: Attachment[] }) =>
          setFormData(value)
        }
        formData={formData}
        disabled={disabled}
        dataIds={{
          textAreaId:
            'assignmentsList_assignmentDrawer_recentActivityTabAddCommentInput',
          submitButtonId:
            'assignmentsList_assignmentDrawer_recentActivityTabAddCommentSendButton',
        }}
      />
    </div>
  )
}

export default AddComment
